import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/PaymentProfilesService';
import { useState } from 'react';

export const usePaymentProfiles = (filterInput: { customerId?: string; billingProviderId?: string }) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});

    const sort = { column: 'created', direction: 'desc' as const };

    const buildFilter = () => {
        const filter: Record<string, string> = {};
        if (filterInput.customerId) filter.customerId = filterInput.customerId;
        if (filterInput.billingProviderId) filter.billingProviderId = filterInput.billingProviderId;
        return Object.keys(filter).length > 0 ? filter : undefined;
    };

    // INFINITE QUERY
    const paymentProfilesQuery = useInfiniteQuery({
        queryKey: ['payment-profiles', filterInput.customerId, filterInput.billingProviderId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({
            limit,
            sort,
            nextToken: pageParam,
            filter: buildFilter()
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payment-profiles'] });
        }
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({ queryKey: ['payment-profiles'] });
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = paymentProfilesQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: paymentProfilesQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: paymentProfilesQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};